/**
 * O que cada card do funil é no SISAC (#1151, ADR-0031): a consulta ou o exame,
 * o serviço, o médico, quando foi agendado, o dia marcado e o valor.
 *
 * Lido AO VIVO pela parte `detalhes_dos_atendimentos` da agenda da clínica, a
 * partir das referências que as rotinas ligaram ao card
 * (`sisac:consulta:<movimento>`, `sisac:agenda:<id>`, `sisac:exame:<movimento>`).
 * Nada do SISAC é gravado no CRM.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

/** A hora da clínica, sem fuso, como o SISAC guarda: a tela mostra como veio. */
const HoraDaClinicaSchema = z.string().datetime({ offset: true, local: true });

export const AtendimentoDoSisacSchema = z.object({
  /** A referência do card, como a rotina a ligou. */
  referencia: z.string(),
  /**
   * - `consulta`: a consulta atendida;
   * - `exame_agendado`: o exame marcado na agenda (o SISAC não diz qual exame é);
   * - `exame_atendido`: o exame da agenda a que o paciente veio;
   * - `exame`: o exame feito, com o nome;
   * - `falta`: o paciente não veio ao exame marcado.
   */
  tipo: z.enum(['consulta', 'exame_agendado', 'exame_atendido', 'exame', 'falta']),
  /**
   * A especialidade da consulta, o nome do exame feito, ou o do exame marcado quando a
   * recepção o escreveu na observação da agenda (`servico_da_observacao`); "Exame
   * agendado" quando nada diz qual é.
   */
  servico: z.string().nullable(),
  /**
   * O serviço do exame marcado veio da observação da agenda (#1151): o SISAC não registra
   * qual exame foi marcado, e a operação da clínica reconhece o que a recepção escreveu
   * ("USG MAMAS") no catálogo de preços. O valor, então, é o PREVISTO, pela tabela, e não
   * entra no total. O texto da observação nunca chega aqui.
   */
  servico_da_observacao: z.boolean(),
  medico: z.string().nullable(),
  /** Quando a recepção marcou (`Agenda.DataSist`). */
  agendado_em: HoraDaClinicaSchema.nullable(),
  /** O dia e a hora do atendimento: o marcado na agenda, ou o do exame feito. */
  marcado_para: HoraDaClinicaSchema.nullable(),
  /**
   * O que o SISAC cobrou; no exame marcado reconhecido pela observação, o preço previsto
   * da tabela; `null` sem nenhum dos dois.
   */
  valor: z.object({ quantia_micros: z.number().int(), moeda: z.string() }).nullable(),
  /** Quando a rotina ligou esta referência ao card: a ordem dos passos. */
  ligado_em: z.string().datetime({ offset: true }),
});
export type AtendimentoDoSisac = z.infer<typeof AtendimentoDoSisacSchema>;

/**
 * Por que a lista vem curta:
 * - `ok`: o SISAC respondeu;
 * - `sem_parte`: a organização não é clínica, ou a parte
 *   `detalhes_dos_atendimentos` não está ligada (a tela não mostra nada);
 * - `fora`: o SISAC não respondeu.
 */
export const SituacaoDoSisacSchema = z.enum(['ok', 'sem_parte', 'fora']);

export const AtendimentosDoFunilSchema = z.object({
  /** O último passo de cada card do quadro que tem referência do SISAC. */
  cards: z.array(
    z.object({
      /** O `referencia_externa` do card no quadro. */
      card: z.string(),
      atendimento: AtendimentoDoSisacSchema,
    }),
  ),
  sisac: SituacaoDoSisacSchema,
});
export type AtendimentosDoFunil = z.infer<typeof AtendimentosDoFunilSchema>;

export const AtendimentosDaOportunidadeSchema = z.object({
  oportunidade_id: IdSchema,
  /** Todos os passos, na ordem em que chegaram ao card: a consulta, depois os exames. */
  itens: z.array(AtendimentoDoSisacSchema),
  /** A soma do que o SISAC cobrou nos passos, sem os previstos; `null` sem nenhum valor. */
  total: z.object({ quantia_micros: z.number().int(), moeda: z.string() }).nullable(),
  sisac: SituacaoDoSisacSchema,
});
export type AtendimentosDaOportunidade = z.infer<typeof AtendimentosDaOportunidadeSchema>;
